import { useEffect, useState } from 'react';
import { Icon } from '../Icon.tsx';
import { fmtClock, fmtTime } from '../../lib/format.ts';
import type { User } from '@shared/types.ts';

type Props = {
  kind: 'sleep' | 'breast' | 'pump';
  label: string;
  startedAt: number;
  by: User | undefined;
  onEnd: () => void;
  onAdjust?: () => void;
  // breast-only
  onSwitch?: () => void;
  switchLabel?: string;
  onPauseToggle?: () => void;
  isPaused?: boolean;
  /** Unix seconds since the current pause started. Drives the mm:ss in the header. */
  pausedSince?: number;
  /** Live secondary line, ticked once a second from inside the card so it
   *  stays current without a parent re-render. */
  getDetail?: (nowSec: number) => string | undefined;
};

export function TimerCard({
  kind,
  label,
  startedAt,
  by,
  onEnd,
  onAdjust,
  onSwitch,
  switchLabel,
  onPauseToggle,
  isPaused,
  pausedSince,
  getDetail,
}: Props) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = window.setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(id);
  }, []);
  const secs = Math.max(0, now - startedAt);
  const detail = getDetail?.(now);
  const pausedSecs = isPaused && pausedSince != null ? Math.max(0, now - pausedSince) : 0;

  const accent =
    kind === 'sleep' ? 'var(--sleep-night)' : kind === 'breast' ? 'var(--breast)' : 'var(--pump)';

  return (
    <div
      className="lt-card"
      style={{
        margin: '0 16px 12px',
        padding: '18px 18px 16px',
        borderRadius: 24,
        background: `linear-gradient(180deg, color-mix(in oklab, ${accent} 14%, transparent), transparent 70%), var(--bg-card)`,
        borderColor: `color-mix(in oklab, ${accent} 25%, var(--border))`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span
          className={isPaused ? '' : 'lt-pulse'}
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: isPaused ? 'var(--text-3)' : accent,
          }}
        />
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: 0.02,
            color: isPaused ? 'var(--text-3)' : accent,
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
          {isPaused && ' · paused'}
        </span>
        {isPaused && pausedSince != null && (
          <span
            className="lt-mono lt-tabular"
            style={{
              fontSize: 12,
              color: 'var(--text-3)',
              letterSpacing: 0.02,
            }}
          >
            {String(Math.floor(pausedSecs / 60)).padStart(2, '0')}:
            {String(pausedSecs % 60).padStart(2, '0')}
          </span>
        )}
      </div>
      <div className="lt-serif lt-tabular" style={{ fontSize: 52, fontWeight: 400, letterSpacing: -0.02 }}>
        {fmtClock(secs)}
      </div>
      <div
        style={{
          marginTop: 2,
          marginBottom: 14,
          fontSize: 13,
          color: 'var(--text-2)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
        }}
      >
        started <span className="lt-mono lt-tabular">{fmtTime(startedAt)}</span>
        {by && (
          <>
            <span style={{ color: 'var(--text-3)' }}>·</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 999,
                  background: by.color ?? 'var(--accent)',
                  color: 'white',
                  fontSize: 9,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {by.name[0]?.toUpperCase()}
              </span>
              {by.name}
            </span>
          </>
        )}
        {detail && (
          <>
            <span style={{ color: 'var(--text-3)' }}>·</span>
            <span style={{ color: 'var(--text-2)' }}>{detail}</span>
          </>
        )}
      </div>
      {/* Row 1: End on its own (most prominent action) */}
      <button
        type="button"
        className="lt-btn lt-btn-primary"
        style={{ width: '100%', padding: '13px 0' }}
        onClick={onEnd}
      >
        <Icon.stop s={16} /> End {kind === 'pump' ? 'pump' : kind === 'breast' ? 'feed' : 'sleep'}
      </button>
      {/* Row 2: secondary actions — Pause/Resume, Switch, Adjust */}
      {(onPauseToggle || onSwitch || onAdjust) && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          {onPauseToggle && (
            <button
              type="button"
              className="lt-btn lt-btn-soft"
              style={{ flex: 1, padding: '11px 0', fontSize: 14 }}
              onClick={onPauseToggle}
              title={isPaused ? 'Resume feeding' : 'Pause feeding'}
            >
              {isPaused ? (
                <>
                  <Icon.play s={14} /> Resume
                </>
              ) : (
                <>
                  <Icon.pause s={14} /> Pause
                </>
              )}
            </button>
          )}
          {onSwitch && (
            <button
              type="button"
              className="lt-btn lt-btn-soft"
              style={{ flex: 1, padding: '11px 0', fontSize: 14 }}
              onClick={onSwitch}
              title="Switch breast — counts as one feed"
            >
              <Icon.undo s={14} /> {switchLabel ?? 'Switch'}
            </button>
          )}
          {onAdjust && (
            <button
              type="button"
              className="lt-btn lt-btn-soft"
              style={{ flex: 1, padding: '11px 0', fontSize: 14 }}
              onClick={onAdjust}
            >
              Adjust
            </button>
          )}
        </div>
      )}
    </div>
  );
}
